import React, { useContext, useEffect } from 'react';
import { Button, Grid, makeStyles } from '@material-ui/core';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-fetching-library';
import PropTypes from 'prop-types';
import Skeleton from '@material-ui/lab/Skeleton';
import { useDispatch, useSelector } from 'react-redux';
import { ActivePatientContext } from '../../../contexts/ActivePatientProvider';
import useMetadataUpdate from '../../../hooks/useMetadataUpdate';
import { getLocalizedDate } from '../../../helpers/localize';
import { formatPhoneNumber } from '../../../helpers/phone';
import { openPatientPane } from '../../../redux_store/reducer/reducers/infoPaneReducer';

const useStyles = makeStyles((theme) => ({
  dot: {
    padding: theme.spacing(0, 0.5),
    color: theme.colors.lightBlue,
  },
  patientInfoBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginTop: 12,
  },
  titleTextBox: {
    display: 'flex',
    maxHeight: 35,
    maxWidth: 700,
    paddingTop: 8,
    width: '100%',
    minWidth: 200,
    alignItems: 'flex-start',
    flexDirection: 'column',

    '@media print': {
      display: 'none',
    },
  },
  patientData: {
    display: 'flex',
  },
}));

const PatientInfoBlock = ({ isReportPage, isForInitLoading, patientUUID }) => {
  const classes = useStyles();
  const {
    patientUuid: paramsPatientUuid,
    accountUuid: paramAccountUuid,
  } = useParams();
  const dispatch = useDispatch();
  const isPatientPage = Boolean(paramsPatientUuid) || isReportPage;
  const { en: { infoPane } } = useSelector((state) => state.i18n);
  const {
    patientUuid,
    patientInfo,
    setPatientUuid,
    setPatientInfo,
  } = useContext(ActivePatientContext);

  const { query } = useMetadataUpdate(useQuery, [
    {
      method: 'GET',
      endpoint: `/accounts/${paramAccountUuid}/patients/${
        paramsPatientUuid || patientUUID
      }`,
    },
    false,
  ]);

  useEffect(() => {
    const hasParamsAccountUuid = !!paramAccountUuid;
    const hasPropsPatientUUID = !!patientUUID;
    const hasParamsPatientUUID = !!paramsPatientUuid;
    const hasPagePatientUUID = hasPropsPatientUUID || hasParamsPatientUUID;
    const patientUuidDontMatch = hasParamsPatientUUID
      ? patientUuid !== paramsPatientUuid
      : patientUuid !== patientUUID;
    const hasEnoughInfoForRequest = hasParamsAccountUuid && hasPagePatientUUID;
    const shouldMakeRequest =
      (isPatientPage && patientUuidDontMatch) || !patientInfo;

    if (hasEnoughInfoForRequest && shouldMakeRequest) {
      if (patientInfo) setPatientInfo(undefined);
      if (isPatientPage && isForInitLoading) {
        query().then(({ error, payload }) => {
          if (!error) {
            setPatientUuid(paramsPatientUuid);
            setPatientInfo({
              ...payload,
              dateOfBirth: getLocalizedDate(payload.dateOfBirth, 'UTC'),
              phone: formatPhoneNumber(
                payload.phone != null ? payload.phone.phone : '',
              ),
            });
          }
        });
      }
    }
  }, [patientUuid, paramsPatientUuid, patientUUID]);

  if (isReportPage) {
    return <></>;
  }

  return patientInfo && patientInfo.dateOfBirth
    ? <div className={classes.patientInfoBox}>
        <span key={patientInfo.dateOfBirth}
              className={classes.patientData}
        >
          {patientInfo.dateOfBirth}
        </span>
      <Button
        variant={'outlined'}
        color={'primary'}
        onClick={() => dispatch(openPatientPane({
          patientUUID: patientInfo.uuid,
          accountUUID: paramAccountUuid,
          patientFirstName: patientInfo.firstName,
          patientLastName: patientInfo.lastName,
        }))}
        style={{ padding: '0px 6px' }}
      >
        {infoPane.moreDetails}
      </Button>
    </div>
    : <Grid item className={classes.titleTextBox}>
      <div style={{ width: '50%' }}><Skeleton variant='text' /></div>
      <div style={{ width: '80%' }}><Skeleton variant='text' /></div>
    </Grid>;
};

PatientInfoBlock.propTypes = {
  isReportPage: PropTypes.bool,
  patientUUID: PropTypes.string,
  isForInitLoading: PropTypes.bool,
};

PatientInfoBlock.defaultProps = {
  isReportPage: false,
  isForInitLoading: false,
  patientUUID: null,
};

export default PatientInfoBlock;
